import React, { ReactElement, Suspense } from "react";
import { Router, Switch, Route } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";

import { RestrictedRoute, Loading, ScrollToTop } from "components";
import { handleRouteRedirect } from "utils";
import { GlobalStyle } from "theme/GlobalStyles";
import ThemeProvider from "theme/ThemeProvider";
import { history } from "redux/configureStore";
import { settingInformationDataResponse } from "redux/setting/general/generalSettingStates";

import { privateRoutes, publicRoutes, subRoutes, layouts } from "./configs";

const IndexRouter: React.FC = (): ReactElement => {
  const privatePaths = privateRoutes.map((r) => r.path as string);
  const publicPaths = publicRoutes.map((r) => r.path as string);
  const subPaths = subRoutes.map((r) => r.path as string);

  const settingInformationData = useSelector(settingInformationDataResponse);

  return (
    <Router history={history}>
      <ScrollToTop />
      <AnimatePresence exitBeforeEnter>
        <Switch>
          <Route path={privatePaths}>
            <layouts.dashboard>
              <Switch>
                {privateRoutes.map(({ children, path, ...props }, idx) => (
                  <RestrictedRoute
                    key={`private-${idx}`}
                    exact
                    isPrivate
                    path={path}
                    from="private"
                    {...props}
                  >
                    {children}
                  </RestrictedRoute>
                ))}
              </Switch>
            </layouts.dashboard>
          </Route>

          <Route path={subPaths}>
            <layouts.sub>
              <Switch>
                {subRoutes.map(
                  ({ children, path, restricted, ...props }, idx) => (
                    <RestrictedRoute
                      key={`sub-${idx}`}
                      from="sub"
                      exact
                      restricted={restricted}
                      path={path}
                      {...props}
                    >
                      {children}
                    </RestrictedRoute>
                  )
                )}
              </Switch>
            </layouts.sub>
          </Route>

          <Route path={publicPaths}>
            <layouts.main>
              <Switch>
                {publicRoutes.map(
                  ({ children, path, restricted, ...props }, idx) => (
                    <RestrictedRoute
                      key={`public-${idx}`}
                      exact
                      restricted={restricted}
                      path={path}
                      from="public"
                      {...props}
                    >
                      {children}
                    </RestrictedRoute>
                  )
                )}
              </Switch>
            </layouts.main>
          </Route>

          <Route
            path="*"
            render={({ location }) => {
              const isExcludedPath =
                privatePaths.includes(location.pathname) ||
                publicPaths.includes(location.pathname) ||
                subPaths.includes(location.pathname);

              if (!isExcludedPath) {
                handleRouteRedirect({
                  settingInformationData,
                  history,
                });
              }
              return null;
            }}
          />
        </Switch>
      </AnimatePresence>
    </Router>
  );
};

const App = () => {
  return (
    <Suspense fallback={<Loading width="80" height="80" />}>
      <GlobalStyle />
      <ThemeProvider>
        <IndexRouter />
      </ThemeProvider>
    </Suspense>
  );
};

export default App;
